import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sidebar, PageHeader, formatCurrency } from "@/components/Layout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { usePaywall } from "@/components/PaywallContext";
import { 
  Sparkles, 
  TrendingDown, 
  ShoppingBag, 
  PiggyBank,
  Heart,
  RefreshCw,
  Crown,
  Lock
} from "lucide-react";

const INSIGHT_ICONS = {
  debt: TrendingDown,
  spending: ShoppingBag,
  savings: PiggyBank,
  motivation: Heart,
  tip: Sparkles
};

export default function InsightsPage() {
  const { user } = useAuth();
  const { showPaywall, isPremium } = usePaywall();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [isPremiumFeature, setIsPremiumFeature] = useState(false);

  const currency = user?.currency || 'GBP';

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/insights/generate`, {
        context: "general"
      }, { withCredentials: true });
      
      if (response.data.is_premium_feature) {
        setIsPremiumFeature(true);
        setInsights(response.data.preview_insights || []);
      } else {
        setInsights(response.data.insights || []);
        setIsPremiumFeature(false);
      }
    } catch (error) {
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const regenerateInsights = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/insights/generate`, {
        context: "detailed"
      }, { withCredentials: true });
      
      if (response.data.is_premium_feature) {
        setIsPremiumFeature(true);
        setInsights(response.data.preview_insights || []);
      } else {
        setInsights(response.data.insights || []);
        toast.success('Fresh insights generated!');
      }
    } catch (error) {
      toast.error('Failed to generate insights');
    } finally {
      setGenerating(false);
    }
  };

  const getInsightIcon = (type) => {
    return INSIGHT_ICONS[type] || Sparkles;
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'debt': return 'bg-p2p-error/20 text-p2p-error';
      case 'spending': return 'bg-p2p-gold/20 text-p2p-gold';
      case 'savings': return 'bg-p2p-success/20 text-p2p-success';
      case 'motivation': return 'bg-pink-500/20 text-pink-400';
      default: return 'bg-p2p-teal-light/20 text-p2p-teal-light';
    }
  };

  return (
    <Sidebar>
      <PageHeader 
        title="AI Insights"
        subtitle="Personalized recommendations powered by AI"
        action={
          user?.is_premium && (
            <Button 
              variant="outline" 
              className="border-white/20 text-p2p-gray hover:bg-white/5"
              onClick={regenerateInsights}
              disabled={generating}
              data-testid="regenerate-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
              Refresh Insights
            </Button>
          )
        }
      />

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-p2p-gold"></div>
        </div>
      ) : isPremiumFeature ? (
        <div className="max-w-2xl mx-auto">
          {/* Premium Upsell */}
          <div className="glass-card p-8 rounded-2xl text-center mb-8 border border-p2p-gold/20 bg-gradient-to-b from-p2p-gold/10 to-transparent">
            <div className="w-16 h-16 rounded-full bg-p2p-gold/20 flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-p2p-gold" />
            </div>
            <h2 className="font-outfit text-2xl font-bold text-p2p-gray mb-2">
              Unlock AI-Powered Insights
            </h2>
            <p className="text-p2p-muted mb-6 max-w-md mx-auto">
              Get personalized, AI-generated recommendations to pay off debt faster, 
              optimize spending, and achieve your financial goals
            </p>
            <Link to="/settings">
              <Button className="btn-gold rounded-full px-8" data-testid="upgrade-btn">
                <Sparkles className="w-4 h-4 mr-2" /> Upgrade to Premium
              </Button>
            </Link>
          </div>

          {/* Preview Insights */}
          <div className="space-y-4 opacity-60">
            <h3 className="text-sm text-p2p-muted uppercase tracking-wide">Preview of what you&apos;ll get</h3>
            {insights.map((insight, index) => {
              const Icon = getInsightIcon(insight.type);
              return (
                <div 
                  key={index}
                  className="glass-card p-6 rounded-xl relative overflow-hidden"
                  data-testid={`preview-insight-${index}`}
                >
                  <div className="absolute inset-0 backdrop-blur-sm bg-p2p-dark/50 flex items-center justify-center z-10">
                    <Lock className="w-8 h-8 text-p2p-muted" />
                  </div>
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getInsightColor(insight.type)}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-medium text-p2p-gray mb-1">{insight.title}</h3>
                      <p className="text-sm text-p2p-muted blur-sm">{insight.message}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Benefits */}
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {[
              { icon: TrendingDown, title: "Debt Strategies", desc: "Smart payoff plans" },
              { icon: ShoppingBag, title: "Spending Analysis", desc: "Find savings opportunities" },
              { icon: PiggyBank, title: "Savings Tips", desc: "Grow your wealth faster" }
            ].map((benefit, index) => (
              <div key={index} className="glass-card p-4 rounded-xl text-center">
                <benefit.icon className="w-6 h-6 text-p2p-gold mx-auto mb-2" />
                <h4 className="font-medium text-p2p-gray text-sm">{benefit.title}</h4>
                <p className="text-xs text-p2p-muted">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {insights.map((insight, index) => {
            const Icon = getInsightIcon(insight.type);
            return (
              <div 
                key={index}
                className="glass-card glass-card-hover p-6 rounded-2xl animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`insight-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${getInsightColor(insight.type)}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-outfit font-semibold text-lg text-p2p-gray mb-2">{insight.title}</h3>
                    <p className="text-p2p-muted leading-relaxed">{insight.message}</p>
                  </div>
                </div>
              </div>
            );
          })}

          {insights.length === 0 && (
            <div className="glass-card p-12 rounded-2xl text-center">
              <Sparkles className="w-16 h-16 text-p2p-muted mx-auto mb-4" />
              <h3 className="font-outfit text-xl font-semibold text-p2p-gray mb-2">
                No insights yet
              </h3>
              <p className="text-p2p-muted mb-6">
                Add some bills and income to get personalized recommendations
              </p>
            </div>
          )}
        </div>
      )}
    </Sidebar>
  );
}
